import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { leads } from '@/lib/api';

interface StageDropdownProps {
  leadId: string;
  currentStage: string;
  onStageChange: (newStage: string, reason?: string) => void;
  disabled?: boolean;
}

// Stage definitions with translations
const STAGES = {
  nýr: 'Nýr',
  haft_samband: 'Haft samband',
  skoðun_bókuð: 'Skoðun bókuð',
  tilboð: 'Tilboð',
  kaupsamningur: 'Kaupsamningur',
  glatað: 'Glatað',
} as const;

type StageKey = keyof typeof STAGES;

// Business logic: Valid next stages
const VALID_TRANSITIONS: Record<StageKey, StageKey[]> = {
  nýr: ['haft_samband', 'glatað'],
  haft_samband: ['skoðun_bókuð', 'nýr', 'glatað'],
  skoðun_bókuð: ['tilboð', 'haft_samband', 'nýr', 'glatað'],
  tilboð: ['kaupsamningur', 'skoðun_bókuð', 'haft_samband', 'nýr', 'glatað'],
  kaupsamningur: ['glatað'], // Can only move to lost (rare case)
  glatað: ['nýr'], // Can reopen
};

export function StageDropdown({
  leadId,
  currentStage,
  onStageChange,
  disabled = false,
}: StageDropdownProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showLostDialog, setShowLostDialog] = useState(false);
  const [lostReason, setLostReason] = useState('');

  const token = localStorage.getItem('token') || '';

  // Get valid next stages for current stage
  const validNextStages = VALID_TRANSITIONS[currentStage as StageKey] || [];

  const handleStageSelect = async (newStage: string) => {
    // If moving to "glatað", show confirmation dialog
    if (newStage === 'glatað') {
      setShowLostDialog(true);
      return;
    }

    await updateStage(newStage);
  };

  const handleLostConfirm = async () => {
    if (!lostReason.trim()) {
      toast({
        variant: 'destructive',
        title: 'Villa',
        description: 'Vinsamlegast skráðu ástæðu',
      });
      return;
    }

    await updateStage('glatað', lostReason);
    setShowLostDialog(false);
    setLostReason('');
  };

  const updateStage = async (newStage: string, reason?: string) => {
    setIsLoading(true);

    try {
      // Update lead stage via API
      await leads.update(token, leadId, {
        status: newStage,
        notes: reason
          ? `${reason}\n\n---\nFærði úr "${STAGES[currentStage as StageKey]}" í "${STAGES[newStage as StageKey]}"`
          : undefined,
      });

      // Call parent callback
      onStageChange(newStage, reason);

      toast({
        variant: 'success',
        title: 'Staða uppfærð',
        description: `Ábendingin var færð í "${STAGES[newStage as StageKey]}"`,
      });
    } catch (error) {
      console.error('Failed to update stage:', error);
      toast({
        variant: 'destructive',
        title: 'Villa',
        description: 'Ekki tókst að uppfæra stöðu',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Select
        value={currentStage}
        onValueChange={handleStageSelect}
        disabled={disabled || isLoading}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Veldu stöðu" />
        </SelectTrigger>
        <SelectContent>
          {/* Current stage (always shown) */}
          <SelectItem value={currentStage}>
            {STAGES[currentStage as StageKey]}
          </SelectItem>

          {/* Valid next stages */}
          {validNextStages.map((stage) => (
            <SelectItem key={stage} value={stage}>
              {STAGES[stage]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Confirmation dialog for "glatað" stage */}
      <Dialog open={showLostDialog} onOpenChange={setShowLostDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Færa í "Glatað"</DialogTitle>
            <DialogDescription>
              Vinsamlegast skráðu ástæðu fyrir því að þessi ábendingin var
              glatað. Þetta hjálpar okkur að læra og bæta þjónustuna.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            placeholder="Ástæða fyrir að ábendingin var glatað..."
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            rows={4}
            className="mt-4"
          />

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowLostDialog(false);
                setLostReason('');
              }}
              disabled={isLoading}
            >
              Hætta við
            </Button>
            <Button
              variant="destructive"
              onClick={handleLostConfirm}
              disabled={isLoading || !lostReason.trim()}
            >
              {isLoading ? 'Uppfærir...' : 'Færa í Glatað'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
